import type { LogFields, Logger, LogLevel } from './types'
import { normalizeArguments } from './arguments'
import { isPlainObject, redactFields } from './redact-fields'
import { reportErrorToSentry } from './sentry'

declare global {
  interface ImportMeta {
    // same modifiers as Nuxt's own declaration, which TypeScript requires when both are in scope
    dev: boolean
  }
}

const STORAGE_KEY = 'forgd:log-level'

const SEVERITY: Record<LogLevel, number> = { error: 3, warn: 2, info: 1, debug: 0 }

let levelOverride: LogLevel | undefined

export function setBrowserLogLevel(level: LogLevel | undefined): void {
  levelOverride = level
}

function isLogLevel(value: unknown): value is LogLevel {
  return value === 'error' || value === 'warn' || value === 'info' || value === 'debug'
}

function storedLevel(): LogLevel | undefined {
  try {
    const value: unknown = localStorage.getItem(STORAGE_KEY)
    return isLogLevel(value) ? value : undefined
  } catch {
    return undefined
  }
}

function activeLevel(): LogLevel {
  if (levelOverride) return levelOverride
  if (import.meta.dev === true) return 'debug'
  return storedLevel() ?? 'warn'
}

function buildLogger(name: string, bindings: LogFields): Logger {
  function write(level: LogLevel, first: unknown, second: unknown): void {
    if (SEVERITY[level] < SEVERITY[activeLevel()]) return
    let message: string
    let fields: LogFields
    let noReport: unknown
    // a throwing getter drops the line; logging never throws
    try {
      const call = normalizeArguments(first, second)
      message = call.message
      ;({ __noReport: noReport, ...fields } = { ...bindings, ...call.fields })
    } catch {
      return
    }
    const line = `[${name}] ${message}`
    if (Object.keys(fields).length > 0) {
      console[level](line, fields)
    } else {
      console[level](line)
    }
    // Sentry's console integration already keeps every printed line as a breadcrumb
    if (level === 'error' && noReport !== true) {
      reportErrorToSentry({ logger: name, message, fields, context: undefined })
    }
  }

  return {
    error: (message, fields) => write('error', message, fields),
    warn: (message, fields) => write('warn', message, fields),
    info: (message, fields) => write('info', message, fields),
    debug: (message, fields) => write('debug', message, fields),
    child: (childBindings) => buildLogger(name, { ...bindings, ...childBindings }),
  }
}

export function createLogger(name: string): Logger {
  return buildLogger(name, {})
}

interface ConsoleBreadcrumb {
  category?: string
  data?: { [key: string]: unknown }
}

// Sentry's console breadcrumbs keep the raw console arguments, so pass this as `beforeBreadcrumb` to censor
// sensitive keys in them the same way logged fields are censored in error events
export function redactConsoleBreadcrumb<T extends ConsoleBreadcrumb>(breadcrumb: T): T {
  const args = breadcrumb.data?.['arguments']
  if (breadcrumb.category !== 'console' || !Array.isArray(args)) return breadcrumb
  return {
    ...breadcrumb,
    data: { ...breadcrumb.data, arguments: args.map((arg: unknown) => (isPlainObject(arg) ? redactFields(arg) : arg)) },
  }
}
